import {
  FunctionComponent,
  PropsWithChildren,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useCookies } from 'react-cookie';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ConfigContext, DetailsModalContext } from '../../../../context';
import {
  StoryCard,
  StoryCardContentGallery,
  StoryCardQuoteContent,
  StoryCardType,
} from '../../../../services';
import {
  COLLAPSE_STORAGE_KEY,
  COPY_CUT_COOKIE_KEY,
  CardActionsAdmin,
  CardOpenButton,
  CollapseMode,
  CopyCutAction,
  FeedCardCut,
  IconLabel,
  getCssVar,
  getStoryCardName,
  getStoryCardPreviewUrl,
  useCardDate,
} from '../../../../shared';

import classNames from 'classnames';
import classes from './HorizontalStoryFeedAdminCardWrapper.module.scss';

interface HorizontalStoryAdminFeedCardWrapperProps {
  card: StoryCard;
}

export const HorizontalStoryFeedAdminCardWrapper: FunctionComponent<
  PropsWithChildren<HorizontalStoryAdminFeedCardWrapperProps>
> = memo(({ card, children }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { setDetailsModalData } = useContext(DetailsModalContext);

  const { showPostedTime = true } = useContext(ConfigContext).config.elements.card;

  const [cookies] = useCookies([COPY_CUT_COOKIE_KEY]);

  const { cardId: copyCutCardId = 0, action: copyCutAction = '' } = {
    ...cookies[COPY_CUT_COOKIE_KEY],
  };

  const [collapseMode, setCollapseMode] = useState(
    localStorage.getItem(COLLAPSE_STORAGE_KEY) || CollapseMode.STANDARD
  );

  useEffect(() => {
    window.addEventListener('storage', () => {
      setCollapseMode(localStorage.getItem(COLLAPSE_STORAGE_KEY) || CollapseMode.STANDARD);
    });
  }, []);

  const isCollapsed = useMemo(() => collapseMode === CollapseMode.COLLAPSED, [collapseMode]);

  const {
    id: cardId,
    storyId,
    type,
    dateToDisplay,
    content,
    externalTags,
    externalStatusTags,
  } = card;

  const date = useCardDate(dateToDisplay);

  const {
    styles: { backgroundColor },
  } = content;

  const { gallery } = content as StoryCardContentGallery;

  const { quoteSource } = content as StoryCardQuoteContent;

  const previewUrl = getStoryCardPreviewUrl(card);

  const isImageGallery = useMemo(
    () =>
      [StoryCardType.IMAGE, StoryCardType.QUOTE].includes(type) && gallery && gallery.length > 1,
    [gallery, type]
  );

  const shouldRenderOverlay = useMemo(
    () =>
      isImageGallery ||
      (previewUrl &&
        [StoryCardType.VIDEO, StoryCardType.AUDIO, StoryCardType.PDF, StoryCardType.POLL].includes(
          type
        )),
    [isImageGallery, previewUrl, type]
  );

  const isCutCard = useMemo(
    () => copyCutAction === CopyCutAction.CUT && cardId === copyCutCardId,
    [cardId, copyCutAction, copyCutCardId]
  );

  const cardClickHandler = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if ((event.target as HTMLElement).tagName.toLowerCase() === 'a') {
        event.preventDefault();
      }

      isMobileOnly
        ? navigate(`/admin/details/${storyId}/${cardId}`)
        : setDetailsModalData({ storyId, cardId });
    },
    [cardId, navigate, setDetailsModalData, storyId]
  );

  const hasTagsIcon = useMemo(
    () => externalTags.length > 0 || externalStatusTags.length > 0,
    [externalTags, externalStatusTags]
  );

  const tagsClickHandler = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.stopPropagation();

      isMobileOnly
        ? navigate(`/admin/details/${storyId}/${cardId}`)
        : setDetailsModalData({ storyId, cardId });
    },
    [cardId, navigate, setDetailsModalData, storyId]
  );

  const iconId = useMemo(() => {
    switch (type) {
      case StoryCardType.ARTICLE:
      case StoryCardType.POST:
        return 'article';
      case StoryCardType.IMAGE:
        return isImageGallery ? 'gallery' : type.toLowerCase();
      case StoryCardType.QUOTE:
        return isImageGallery ? 'gallery' : quoteSource?.toLowerCase();
      default:
        return type.toLowerCase();
    }
  }, [isImageGallery, quoteSource, type]);

  const iconColor = useMemo(
    () => (!previewUrl ? getCssVar('--card-overlay-icon-color') : '#ffffff'),
    [previewUrl]
  );

  if (isCutCard) {
    return <FeedCardCut isCollapsed={isCollapsed} isHorizontalFeedCard />;
  }

  return (
    <>
      <div
        className={classNames(`card-${cardId}`, classes['card'], {
          [classes['card--collapsed']]: isCollapsed,
        })}
        style={{ backgroundColor }}
        onClick={cardClickHandler}
      >
        <CardOpenButton label={getStoryCardName({ card, t })} />

        <div
          className={classNames(classes['card__teaser'], {
            [classes['card__teaser--placeholder']]: !previewUrl,
            [classes['card__teaser--overlay']]: shouldRenderOverlay,
          })}
          {...(previewUrl && { style: { backgroundImage: `url(${previewUrl})` } })}
        >
          {(!previewUrl || shouldRenderOverlay) && (
            <IconLabel
              className={classes['card__teaser-icon']}
              iconId={iconId}
              iconSize={32}
              color={iconColor}
              singleColor
            />
          )}
        </div>
        <div
          className={classNames(classes['card__content'], {
            [classes['card__content--collapsed']]: isCollapsed,
          })}
        >
          {!isCollapsed && children}
          {showPostedTime && date && <div className={classes['card__posted-time']}>{date}</div>}
        </div>
      </div>

      {hasTagsIcon && (
        <IconLabel
          className={classes['card__tags']}
          iconSize={18}
          iconId={'tags'}
          ariaLabel={t('cardActions.show-tags')}
          color={getCssVar('--action-pane-icon-color')}
          hoverColor={getCssVar('--action-pane-icon-hover-color')}
          onClick={tagsClickHandler}
        />
      )}

      <CardActionsAdmin card={card} isHorizontalFeedCard />
    </>
  );
});
